import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { fetchMyPermissions, toggleUserStatus } from "../../../redux/slices/userSlice";

const getErrorMessage = (error, fallback) => {
  const detail = error?.payload?.detail || error?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
        return field ? `${field}: ${item.msg || item.message || ""}` : item.msg || item.message || "";
      })
      .filter(Boolean)
      .join(" | ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return error?.message || fallback;
};

const OtherDetailsTab = ({ currentUser, userId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const loggedInUser = useSelector((state) => state.auth?.user);
  const permissions = useSelector((state) => state.users?.permissions || {});
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const canManageAccount = permissions.can_delete_users === true;
  const isCurrentUser = String(loggedInUser?.id) === String(userId);

  useEffect(() => {
    dispatch(fetchMyPermissions());
  }, [dispatch]);

  if (!canManageAccount) {
    return (
      <Alert variant="warning" className="mt-3">
        {t("components.OtherDetailsTab.superAdminOnly", "Apenas o Super Admin pode gerir estas opções.")}
      </Alert>
    );
  }

  if (!currentUser || !userId) return null;

  const isActive = currentUser.is_active !== false;

  const handleStatusChange = async () => {
    if (isChangingStatus || isCurrentUser) return;

    const nextStatus = !isActive;
    const confirmationMessage = nextStatus
      ? t("components.OtherDetailsTab.confirmActivate", "Ativar esta conta?")
      : t("components.OtherDetailsTab.confirmDeactivate", "Desativar esta conta?");

    const confirmed =
      typeof window.confirm === "function" ? window.confirm(confirmationMessage) : true;
    if (!confirmed) return;

    setIsChangingStatus(true);
    setFeedback(null);

    try {
      await dispatch(toggleUserStatus({ userId, status: nextStatus })).unwrap();

      setFeedback({
        type: "success",
        message: nextStatus
          ? t("components.OtherDetailsTab.activated", "Conta ativada.")
          : t("components.OtherDetailsTab.deactivated", "Conta desativada."),
      });
    } catch (error) {
      setFeedback({
        type: "danger",
        message: getErrorMessage(
          error,
          t("components.OtherDetailsTab.statusError", "Não foi possível alterar o estado da conta.")
        ),
      });
    } finally {
      setIsChangingStatus(false);
    }
  };

  return (
    <div className="pt-3 pb-4">
      <div className="mb-4">
        <h3 className="txt-main fw-bold mb-1">
          {t("components.OtherDetailsTab.title", "Estado da conta")}
        </h3>
        <p className="txt-subtle mb-0" style={{ fontSize: "0.9rem" }}>
          {t(
            "components.OtherDetailsTab.description",
            "Ative ou desative o acesso da conta. Os dados organizacionais e o contrato são geridos nos separadores Labor Details e Contracts."
          )}
        </p>
      </div>

      {feedback && (
        <Alert variant={feedback.type} role="alert">
          {feedback.message}
        </Alert>
      )}

      <Card className="atlar-card p-4">
        <Card.Body className="p-0">
          <div className="d-flex justify-content-between align-items-center gap-3">
            <div>
              <h5 className="txt-main fw-bold mb-1">
                {t("components.OtherDetailsTab.accountStatus", "Estado da conta")}
              </h5>
              <p className="txt-subtle mb-0" style={{ fontSize: "0.85rem" }}>
                {isActive
                  ? t("components.OtherDetailsTab.active", "Conta ativa")
                  : t("components.OtherDetailsTab.inactive", "Conta desativada")}
              </p>
            </div>

            <Button
              type="button"
              variant={isActive ? "outline-danger" : "outline-success"}
              onClick={handleStatusChange}
              disabled={isChangingStatus || isCurrentUser}
            >
              {isChangingStatus ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                  aria-hidden="true"
                />
              ) : isActive ? (
                t("components.OtherDetailsTab.deactivate", "Desativar conta")
              ) : (
                t("components.OtherDetailsTab.activate", "Ativar conta")
              )}
            </Button>
          </div>

          {isCurrentUser && (
            <small className="txt-subtle d-block mt-3">
              {t(
                "components.OtherDetailsTab.selfStatus",
                "Não podes desativar a tua própria conta."
              )}
            </small>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default OtherDetailsTab;
